import { safeJsonParse } from '../../../utils/safeJsonParse.js';

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    display:block;
    box-sizing: border-box;
    font-family: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    color: var(--ctl-color-text);
    min-width: 0;
  }

  nav{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    min-width: 0;
  }

  ol{
    list-style: none;
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    padding: 0;
    margin: 0;
    min-width: 0;
    flex-wrap: wrap;
  }

  li{
    display:inline-flex;
    align-items:center;
    gap: var(--ctl-space-2);
    min-width: 0;
  }

  a, button.linkish{
    all: unset;
    cursor: pointer;
    color: color-mix(in srgb, var(--ctl-color-brand) 85%, var(--ctl-color-text));
    font-weight: var(--ctl-font-weight-semibold);
    font-size: var(--ctl-font-size-md);
    line-height: var(--ctl-leading-tight);
    border-radius: var(--ctl-radius-md);
    padding: var(--ctl-space-2) var(--ctl-space-2);
    margin: calc(-1 * var(--ctl-space-2)) calc(-1 * var(--ctl-space-2)); /* easier click target without changing layout */
    white-space: nowrap;
    max-width: 36ch;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  a:hover, button.linkish:hover{
    background: color-mix(in srgb, var(--ctl-color-border) 35%, transparent);
  }

  a:focus-visible, button.linkish:focus-visible{
    outline: none;
    box-shadow: var(--ctl-focus-ring);
  }

  .current{
    color: var(--ctl-color-text);
    font-weight: var(--ctl-font-weight-extrabold);
    cursor: default;
  }

  .disabled{
    color: color-mix(in srgb, var(--ctl-color-muted) 90%, var(--ctl-color-text));
    opacity: var(--ctl-opacity-muted);
    cursor: not-allowed;
  }

  .sep{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    color: var(--ctl-color-muted);
    opacity: var(--ctl-opacity-interactive);
    user-select: none;
  }

  /* default separators */
  .sep svg{ width: var(--ctl-space-3-5); height: var(--ctl-space-3-5); display:block; }

  /* Collapsed middle */
  details{
    position: relative;
    display:inline-flex;
    align-items:center;
  }

  summary{
    list-style: none;
    cursor: pointer;
    border-radius: var(--ctl-radius-md);
    padding: var(--ctl-space-2) var(--ctl-space-2);
    margin: calc(-1 * var(--ctl-space-2)) calc(-1 * var(--ctl-space-2));
    color: var(--ctl-color-muted);
    font-weight: var(--ctl-font-weight-extrabold);
  }
  summary::-webkit-details-marker{ display:none; }
  summary:hover{ background: color-mix(in srgb, var(--ctl-color-border) 35%, transparent); }
  summary:focus-visible{ outline:none; box-shadow: var(--ctl-focus-ring); }

  .menu{
    position: absolute;
    top: calc(100% + var(--ctl-space-2));
    left: 0;
    min-width: 220px;
    max-width: min(360px, 70vw);
    background: var(--ctl-color-surface-inverse);
    color: var(--ctl-color-on-surface-inverse);
    border-radius: var(--ctl-radius-lg);
    padding: var(--ctl-space-2);
    box-shadow: var(--ctl-shadow-dropdown);
    z-index: var(--ctl-z-dropdown);
  }

  .menu a, .menu button{
    all: unset;
    display:block;
    cursor:pointer;
    padding: var(--ctl-space-2) var(--ctl-space-2);
    border-radius: var(--ctl-radius-lg);
    font-size: var(--ctl-font-size-md);
    line-height: var(--ctl-leading-tight);
    white-space: nowrap;
    overflow:hidden;
    text-overflow: ellipsis;
  }
  .menu a:hover, .menu button:hover{
    background: var(--ctl-color-overlay-5);
  }
  .menu a:focus-visible, .menu button:focus-visible{
    outline:none;
    box-shadow: var(--ctl-focus-ring);
  }

  /* Hide template slot content if used later */
  ::slotted(template){ display:none; }
</style>

<nav aria-label="Breadcrumb" part="nav">
  <ol id="list" part="list"></ol>
</nav>

<slot name="separator" hidden></slot>
<slot name="ellipsis" hidden></slot>
`;

function toArray(v){ return Array.isArray(v) ? v : []; }

function clampSeparator(v){
  return (v === "slash" || v === "dot") ? v : "chevron";
}

function clampInt(v, d){
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(1, Math.min(50, Math.floor(n))) : d;
}

function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function sepSvg(type){
  if (type === "slash") {
    return `<span class="sep" aria-hidden="true">/</span>`;
  }
  if (type === "dot") {
    return `<span class="sep" aria-hidden="true">•</span>`;
  }
  // chevron
  return `
    <span class="sep" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path fill="currentColor" d="M9.29 6.71a1 1 0 0 0 0 1.41L13.17 12l-3.88 3.88a1 1 0 1 0 1.42 1.41l4.58-4.58a1 1 0 0 0 0-1.41L10.7 6.7a1 1 0 0 0-1.41.01z"/>
      </svg>
    </span>
  `;
}

export class CtlBreadcrumbs extends HTMLElement {
  static get observedAttributes() {
    return ["items", "separator", "max-items"];
  }

  constructor(){
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._list = this.shadowRoot.getElementById("list");

    this._items = [];
    this._onClick = this._onClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  connectedCallback(){
    if (!this.hasAttribute("separator")) this.setAttribute("separator", "chevron");
    if (!this.hasAttribute("max-items")) this.setAttribute("max-items", "5");

    if (this.hasAttribute("items")) this.items = safeJsonParse(this.getAttribute("items"), []);
    this.shadowRoot.addEventListener("click", this._onClick);
    this.shadowRoot.addEventListener("keydown", this._onKeyDown);
    this._render();
  }

  disconnectedCallback(){
    this.shadowRoot.removeEventListener("click", this._onClick);
    this.shadowRoot.removeEventListener("keydown", this._onKeyDown);
  }

  attributeChangedCallback(name){
    if (name === "items") this.items = safeJsonParse(this.getAttribute("items"), []);
    if (name === "separator" || name === "max-items") this._render();
  }

  // ---- Props ----
  get items(){ return this._items; }
  set items(v){
    this._items = toArray(v).map((it, idx) => {
      const obj = (it && typeof it === "object") ? it : {};
      const label = String(obj.label ?? "");
      const href = obj.href != null ? String(obj.href) : null;
      const id = obj.id != null ? String(obj.id) : null;
      const current = !!obj.current || idx === (v?.length ? v.length - 1 : idx);
      const disabled = !!obj.disabled;
      return { label, href, id, current, disabled };
    }).filter((x) => x.label);

    this._render();
  }

  get separator(){ return clampSeparator(this.getAttribute("separator")); }
  set separator(v){ this.setAttribute("separator", clampSeparator(v)); }

  get maxItems(){ return clampInt(this.getAttribute("max-items"), 5); }
  set maxItems(v){ this.setAttribute("max-items", String(clampInt(v, 5))); }

  // ---- Render ----
  _render(){
    const sepType = this.separator;
    const max = this.maxItems;
    const items = this._items.length ? this._items : [];

    // Determine which items show (collapse middle)
    let visible = items;
    let collapsed = [];
    if (items.length > max) {
      // keep first 2 and last (max-2), collapse the middle
      const keepHead = 2;
      const keepTail = Math.max(1, max - keepHead - 1);
      const head = items.slice(0, keepHead);
      const tail = items.slice(items.length - keepTail);
      collapsed = items.slice(keepHead, items.length - keepTail);
      visible = [...head, { _ellipsis: true }, ...tail];
    }

    const html = visible.map((it, i) => {
      const isLast = i === visible.length - 1;

      let crumbHtml = "";
      if (it._ellipsis) {
        crumbHtml = this._renderEllipsis(collapsed);
      } else {
        const label = escapeHtml(it.label);
        const isCurrent = !!it.current || isLast;
        const isDisabled = !!it.disabled;

        if (it.href && !isCurrent && !isDisabled) {
          crumbHtml = `<a href="${escapeHtml(it.href)}" data-role="crumb" data-index="${i}" data-id="${escapeHtml(it.id || "")}">${label}</a>`;
        } else {
          // Use button for SPA-like click capture even without href; but make current/disabled non-interactive.
          const cls = isCurrent ? "current" : (isDisabled ? "disabled" : "");
          const ariaCurrent = isCurrent ? ` aria-current="page"` : "";
          const ariaDisabled = isDisabled ? ` aria-disabled="true"` : "";
          const tabIndex = (isCurrent || isDisabled) ? ` tabindex="-1"` : ` tabindex="0"`;
          crumbHtml = `<button class="linkish ${cls}" type="button" data-role="crumb" data-index="${i}" data-id="${escapeHtml(it.id || "")}"${ariaCurrent}${ariaDisabled}${tabIndex}>${label}</button>`;
        }
      }

      const sep = isLast ? "" : (this._hasCustomSeparator()
        ? `<span class="sep" part="separator"><slot name="separator"></slot></span>`
        : sepSvg(sepType));

      return `<li part="item">${crumbHtml}${sep}</li>`;
    }).join("");

    this._list.innerHTML = html;
  }

  _hasCustomSeparator(){
    // if user provides slot=separator content, use it
    const slot = this.shadowRoot.querySelector('slot[name="separator"]');
    const assigned = slot?.assignedNodes?.({ flatten: true }) || [];
    return assigned.length > 0;
  }

  _renderEllipsis(collapsedItems){
    const labelSlot = this.shadowRoot.querySelector('slot[name="ellipsis"]');
    const hasEllipsisSlot = (labelSlot?.assignedNodes?.({ flatten: true }) || []).length > 0;

    const menuItems = collapsedItems.map((it) => {
      const label = escapeHtml(it.label);
      const href = it.href ? escapeHtml(it.href) : "";
      const id = escapeHtml(it.id || "");
      const disabled = !!it.disabled;

      if (href && !disabled) {
        return `<a href="${href}" data-role="menu-item" data-href="${href}" data-id="${id}">${label}</a>`;
      }
      // non-link / disabled
      const ariaDisabled = disabled ? ` aria-disabled="true"` : "";
      const tabIndex = disabled ? ` tabindex="-1"` : ` tabindex="0"`;
      return `<button type="button" data-role="menu-item" data-href="${href}" data-id="${id}"${ariaDisabled}${tabIndex}>${label}</button>`;
    }).join("");

    return `
      <details data-role="ellipsis">
        <summary aria-label="Show hidden breadcrumbs">
          ${hasEllipsisSlot ? `<slot name="ellipsis"></slot>` : "…"}
        </summary>
        <div class="menu" role="menu" aria-label="Hidden breadcrumbs">
          ${menuItems}
        </div>
      </details>
    `;
  }

  // ---- Interaction ----
  _onClick(e){
    const t = e.target;
    if (!t) return;

    // Close ellipsis menu when clicking a menu item
    const menuItem = t.closest?.("[data-role='menu-item']");
    if (menuItem) {
      const href = menuItem.getAttribute("data-href") || "";
      const id = menuItem.getAttribute("data-id") || "";
      // attempt to find matching item
      const item = this._items.find((x) => (id && x.id === id) || (href && x.href === href)) || null;

      if (href) {
        const ev = new CustomEvent("ctl-navigate", {
          bubbles: true,
          composed: true,
          cancelable: true,
          detail: { href, id: id || null, item }
        });
        const ok = this.dispatchEvent(ev);
        if (!ok) e.preventDefault();
      }

      // close details
      const details = this.shadowRoot.querySelector("details[data-role='ellipsis']");
      if (details) details.open = false;
      return;
    }

    // Normal crumb click
    const crumb = t.closest?.("[data-role='crumb']");
    if (!crumb) return;

    // If it's an <a>, let default happen unless intercepted
    const isAnchor = crumb.tagName === "A";
    const href = isAnchor ? crumb.getAttribute("href") : null;
    const id = crumb.getAttribute("data-id") || null;

    // Determine item by href/id/label
    const item = this._items.find((x) => (id && x.id === id) || (href && x.href === href) || x.label === crumb.textContent) || null;
    const disabled = item?.disabled;

    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (href) {
      const ev = new CustomEvent("ctl-navigate", {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail: { href, id: id || null, item }
      });
      const ok = this.dispatchEvent(ev);
      if (!ok) e.preventDefault();
    }
  }

  _onKeyDown(e){
    // Make ellipsis menu behave: Escape closes
    if (e.key === "Escape") {
      const details = this.shadowRoot.querySelector("details[data-role='ellipsis']");
      if (details && details.open) {
        e.preventDefault();
        details.open = false;
      }
    }
  }
}

customElements.define("ctl-breadcrumbs", CtlBreadcrumbs);
